import React, { useState, useEffect } from 'react';
import { voiceActorService } from '../../services/voiceActorService';
import { getAllPlays } from '../../services/playsService';

import { realtimeService } from '../../services/realtimeSubscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import { Users, Play, CheckCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';


export default function VoiceActorAssignmentInterface() {
  const { user, userProfile } = useAuth();
  const [voiceActors, setVoiceActors] = useState([]);
  const [plays, setPlays] = useState([]);
  const [selectedPlay, setSelectedPlay] = useState(null);
  const [isPlaySectionMinimized, setIsPlaySectionMinimized] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');

  useEffect(() => {
    loadData();
    setupRealtimeSubscriptions();

    return () => {
      realtimeService?.unsubscribeAll();
    };
  }, []);

  useEffect(() => {
    if (selectedPlay) {
      loadCharacters(selectedPlay?.id);
      setIsPlaySectionMinimized(true);
    }
  }, [selectedPlay]);

  const setupRealtimeSubscriptions = () => {
    setRealtimeStatus('connecting');

    realtimeService?.subscribeToAssignments((payload) => {
      console.log('Assignment update:', payload);
      handleAssignmentUpdate(payload);
    });

    realtimeService?.subscribeToVoiceActorAvailability((payload) => {
      console.log('Voice actor availability update:', payload);
      handleAvailabilityUpdate(payload);
    });

    setRealtimeStatus('connected');
  };

  const handleAssignmentUpdate = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    setAssignments(prev => {
      if (eventType === 'INSERT') {
        showNotification('New assignment created', 'success');
        return [...prev, newRecord];
      } else if (eventType === 'UPDATE') {
        showNotification('Assignment updated', 'info');
        return prev?.map(a => a?.id === newRecord?.id ? newRecord : a);
      } else if (eventType === 'DELETE') {
        showNotification('Assignment removed', 'warning');
        return prev?.filter(a => a?.id !== oldRecord?.id);
      }
      return prev;
    });
  };

  const handleAvailabilityUpdate = (payload) => {
    const { eventType, new: newRecord } = payload;

    if (eventType === 'UPDATE' && newRecord?.is_available !== undefined) {
      setVoiceActors(prev => 
        prev?.map(actor => 
          actor?.id === newRecord?.id 
            ? { ...actor, isAvailable: newRecord?.is_available }
            : actor
        )
      );
    } else {
      loadVoiceActors();
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    const bgColor = 
      type === 'success' ? 'bg-success' : 
      type === 'error' ? 'bg-destructive' : 
      type === 'warning'? 'bg-warning' : 'bg-accent';
    
    notification.className = `fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all transform translate-x-0 ${bgColor} text-white`;
    notification.textContent = message;
    document.body?.appendChild(notification);

    setTimeout(() => {
      notification?.classList?.add('translate-x-full', 'opacity-0');
      setTimeout(() => notification?.remove(), 300);
    }, 3000);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [actorsData, playsData] = await Promise.all([
        voiceActorService?.getAllVoiceActors(),
        getAllPlays()
      ]);
      setVoiceActors(actorsData);
      setPlays(playsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadVoiceActors = async () => {
    try {
      const actorsData = await voiceActorService?.getAllVoiceActors();
      setVoiceActors(actorsData);
    } catch (err) {
      console.error('Error loading voice actors:', err);
    }
  };

  const loadCharacters = async (playId) => {
    try {
      const [charactersData, assignmentsData] = await Promise.all([
        voiceActorService?.getCharactersByPlay(playId),
        voiceActorService?.getCharacterAssignments(playId)
      ]);
      setCharacters(charactersData);
      setAssignments(assignmentsData);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAssignVoiceActor = async (characterName, voiceActorId) => {
    try {
      setError('');
      setSuccess('');
      
      const result = await voiceActorService?.assignVoiceActorToCharacter(
        voiceActorId,
        selectedPlay?.id,
        characterName,
        userProfile?.id
      );

      setSuccess(`Successfully assigned ${result?.linesAssigned} lines to voice actor`);
      
      // Reload characters and assignments
      await loadCharacters(selectedPlay?.id);
    } catch (err) {
      setError(err?.message);
    }
  };

  const getAssignmentForCharacter = (characterName) => {
    return assignments?.find(a => a?.characterName === characterName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading voice actor data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={user}
        isAuthenticated={true}
        userRole={userProfile?.role}
      />
      <BreadcrumbTrail />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">Voice Actor Assignment</h1>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-success text-success-foreground" 
                 style={{ opacity: realtimeStatus === 'connected' ? 1 : 0.5 }}>
              <div className={`w-2 h-2 rounded-full bg-white ${
                realtimeStatus === 'connected' ? 'animate-pulse' : ''
              }`}></div>
              <span>{realtimeStatus === 'connected' ? 'Live' : 'Connecting...'}</span>
            </div>
          </div>
          <p className="mt-2 text-muted-foreground">
            Assign voice actors to character roles in plays. All character lines will be assigned to the selected voice actor.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-success/10 border border-success rounded-lg p-4">
            <p className="text-success">{success}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-card-foreground">{voiceActors?.length}</p>
                <p className="text-sm text-muted-foreground">Voice Actors</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/10 rounded-lg">
                <Play className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold text-card-foreground">{plays?.length}</p>
                <p className="text-sm text-muted-foreground">Available Plays</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/10 rounded-lg">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-card-foreground">{assignments?.length}</p>
                <p className="text-sm text-muted-foreground">Active Assignments</p>
              </div>
            </div>
          </div>
        </div>

        {/* Play Selection */}
        <div className="bg-card border border-border rounded-lg mb-8 overflow-hidden transition-all">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-card-foreground">Select a Play</h2>
              {selectedPlay && (
                <button
                  onClick={() => setIsPlaySectionMinimized(!isPlaySectionMinimized)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                >
                  {isPlaySectionMinimized ? (
                    <>
                      <span>Expand</span>
                      <ChevronDown className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span>Minimize</span>
                      <ChevronUp className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
            
            {isPlaySectionMinimized && selectedPlay ? (
              <div className="flex items-center justify-between p-4 bg-primary/5 border-2 border-primary rounded-lg">
                <div>
                  <h3 className="font-semibold text-card-foreground mb-1">{selectedPlay?.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedPlay?.characterCount || 0} characters • {selectedPlay?.lineCount || 0} lines
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedPlay(null);
                    setIsPlaySectionMinimized(false);
                  }}
                  className="px-4 py-2 text-sm bg-background border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Change Play
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plays?.map(play => (
                  <button
                    key={play?.id}
                    onClick={() => setSelectedPlay(play)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      selectedPlay?.id === play?.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
                    }`}
                  >
                    <h3 className="font-semibold text-card-foreground mb-1">{play?.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {play?.characterCount || 0} characters • {play?.lineCount || 0} lines
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Character Assignment Section */}
        {selectedPlay && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold text-card-foreground mb-6">
              {selectedPlay?.title} - Character Assignments
            </h2>

            {characters?.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No characters found for this play</p>
              </div>
            ) : (
              <div className="space-y-4">
                {characters?.map(character => {
                  const assignment = getAssignmentForCharacter(character?.character);
                  
                  return (
                    <div
                      key={character?.character}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-card-foreground">{character?.character}</h3>
                        <p className="text-sm text-muted-foreground">
                          {character?.lineCount} lines
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {assignment ? (
                          <div className="flex items-center gap-2 px-4 py-2 bg-success/10 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-success" />
                            <span className="text-sm font-medium text-success">
                              {assignment?.voiceActorName}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <select
                              onChange={(e) => {
                                if (e?.target?.value) {
                                  handleAssignVoiceActor(character?.character, e?.target?.value);
                                  e.target.value = '';
                                }
                              }}
                              className="px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              <option value="">Assign Voice Actor...</option>
                              {voiceActors?.map(actor => (
                                <option key={actor?.id} value={actor?.id}>
                                  {actor?.stageName || actor?.fullName}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}